/**
 * Create Package Modal
 *
 * Creates a package (atomic group) via itemRelationshipsStore.createGroup,
 * or adds this item to an existing package. Opened from Item Relationship Inspector.
 *
 * Package is the product-facing name for an atomic group.
 */

import { useState, useMemo, useEffect } from 'react';
import { X, Search, CheckCircle, Circle, Layers, AlertCircle, Plus } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PackageSubtype } from '../../../types/prioritization/itemRelationships';
import { evaluateItemRelationships, type RelationshipEvalContext } from '../../../lib/itemRelationshipRuleEngine';
import { RelationshipGatesBanner } from '../RelationshipGatesBanner';

interface CreatePackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (groupId: string) => void;
  sourceItemId?: string;
}

export function CreatePackageModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: CreatePackageModalProps) {
  const userId = useUserStore((s) => s.userId);
  const createGroup = useItemRelationshipsStore((s) => s.createGroup);
  const addGroupMembers = useItemRelationshipsStore((s) => s.addGroupMembers);
  const getGroupsForItem = useItemRelationshipsStore((s) => s.getGroupsForItem);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const createItem = useItemsStore((s) => s.createItem);

  const [mode, setMode] = useState<'create' | 'add'>('create');
  const [packageTitle, setPackageTitle] = useState('');
  const [prioritizeChecked, setPrioritizeChecked] = useState(true);
  const [selectChecked, setSelectChecked] = useState(false);
  const packageSubtype: PackageSubtype | undefined = prioritizeChecked && selectChecked
    ? 'prioritize_and_select'
    : prioritizeChecked ? 'prioritize'
    : selectChecked ? 'select'
    : undefined;
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>(sourceItemId ? [sourceItemId] : []);
  const [existingPackageId, setExistingPackageId] = useState<string>('');
  const [rationale, setRationale] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const existingPackages = useMemo(
    () => sourceItemId ? getGroupsForItem(sourceItemId).filter((g) => g.type === 'package') : [],
    [getGroupsForItem, sourceItemId],
  );

  // Evaluate Gate 1 (cluster) for every selected item — a Package cannot be created
  // while any member has an unresolved cluster candidate (canon 07A Rule CL-BL-1).
  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);
  const getItemById = useItemsStore((s) => s.getItemById);

  const packageCreationGateOutcomes = useMemo(() => {
    if (mode !== 'create' || selectedItemIds.length < 2) return [];
    const outcomes = selectedItemIds.flatMap((itemId) => {
      const item = getItemById(itemId);
      const ctx: RelationshipEvalContext = {
        itemId,
        allGroups,
        allMemberships,
        allEdges,
        itemFrames: item?.frames ?? [],
        scopeItemIds: null,
        activeFrameId: null,
        declaredTrackRole: null,
        selectedElaborationChildId: null,
        ringAtTime: 3,
        isCommitmentTime: false,
      };
      const result = evaluateItemRelationships(ctx);
      return result.outcomes.filter((o) => o.gate === 1);
    });
    return outcomes;
  }, [mode, selectedItemIds, allGroups, allMemberships, allEdges, getItemById]);

  const packageCreationIsBlocked = packageCreationGateOutcomes.some((o) => o.level === 'block');

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedItemIds(sourceItemId ? [sourceItemId] : []);
    setExistingPackageId('');
    setPackageTitle('');
    setPrioritizeChecked(true);
    setSelectChecked(false);
    setRationale('');
    setSearchQuery('');
    setError(null);
    setMode('create');
  }, [isOpen, sourceItemId]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (i) => i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const handleToggleItem = (itemId: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleCreate = async () => {
    setError(null);

    if (mode === 'add') {
      if (!existingPackageId) {
        setError('Select a package to add this item to');
        return;
      }
      setIsCreating(true);
      try {
        const ok = await addGroupMembers(existingPackageId, sourceItemId ? [sourceItemId] : [], {
          addedBy: userId ?? 'unknown',
          inclusionReason: rationale.trim() || undefined,
        });
        if (ok) {
          onCreated?.(existingPackageId);
          onClose();
        } else {
          setError('Failed to add to package');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add to package');
      } finally {
        setIsCreating(false);
      }
      // 'add' mode is finished either way. This return used to live in the
      // finally block, where it would have swallowed anything the catch
      // re-threw and overridden the control flow of the whole try — the
      // no-unsafe-finally rule exists for exactly that. Out here it just ends
      // the branch.
      return;
    }

    // mode === 'create'
    if (selectedItemIds.length < 2) {
      setError('Select at least 2 items to create a package');
      return;
    }
    if (!rationale.trim()) {
      setError('Provide a rationale for grouping these items as a package');
      return;
    }

    setIsCreating(true);
    try {
      const group = await createGroup({
        type: 'package',
        subtype: packageSubtype,
        title: packageTitle.trim() || undefined,
        memberItemIds: selectedItemIds,
        canonicalItemId: selectedItemIds[0],
        rationale: rationale.trim(),
        createdBy: userId ?? 'unknown',
      });
      if (group) {
        onCreated?.(group.id);
        onClose();
      } else {
        setError('Failed to create package');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create package');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Layers className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Create or Add to Package</h2>
              <p className="text-sm text-[var(--text-ter)]">Atomic groups — prioritize together or select together</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Relationship gate diagnostics — cluster review required for any selected item */}
          {packageCreationGateOutcomes.length > 0 && (
            <RelationshipGatesBanner outcomes={packageCreationGateOutcomes} />
          )}

          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Package Name <span className="text-[var(--text-ter)] font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={packageTitle}
                onChange={(e) => setPackageTitle(e.target.value)}
                placeholder="e.g. Checkout flow must-haves"
                className="w-full px-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-[var(--slate)]"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('create')}
                className={`p-3  border-2 text-left ${
                  mode === 'create' ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="font-medium text-sm text-[var(--text-pri)]">Create new package</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">Start a new atomic group</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('add')}
                disabled={existingPackages.length === 0}
                className={`p-3  border-2 text-left ${
                  mode === 'add' ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="font-medium text-sm text-[var(--text-pri)]">Add to existing package</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">
                  {existingPackages.length > 0 ? `${existingPackages.length} package(s)` : 'No packages yet'}
                </div>
              </button>
            </div>
          </div>

          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Package type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPrioritizeChecked((v) => !v)}
                  className={`flex-1 px-3 py-2  border text-sm font-medium transition-colors ${
                    prioritizeChecked
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--rule)]'
                  }`}
                >
                  Prioritize together
                </button>
                <button
                  type="button"
                  onClick={() => setSelectChecked((v) => !v)}
                  className={`flex-1 px-3 py-2  border text-sm font-medium transition-colors ${
                    selectChecked
                      ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--rule)]'
                  }`}
                >
                  Select together
                </button>
              </div>
            </div>
          )}

          {mode === 'add' ? (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Select package</label>
              <select
                value={existingPackageId}
                onChange={(e) => setExistingPackageId(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-4 py-2 border border-[var(--rule)]"
              >
                <option value="">—</option>
                {existingPackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.title || `Package ${pkg.id.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Select items ({selectedItemIds.length} selected)
              </label>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items..."
                  className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm"
                />
              </div>
              <div className="border border-[var(--rule)] max-h-48 overflow-y-auto">
                {filteredItems.length === 0 && !searchQuery ? (
                  <div className="p-4 text-center text-[var(--text-ter)] text-sm">No items found</div>
                ) : (
                  filteredItems.map((item) => {
                    const isSelected = selectedItemIds.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 border-b border-[var(--rule)] last:border-b-0 hover:bg-[var(--surface)] ${
                          isSelected ? 'bg-[var(--slate-dim)]' : ''
                        }`}
                      >
                        <button onClick={() => handleToggleItem(item.id)} className="flex-shrink-0">
                          {isSelected ? (
                            <CheckCircle className="h-5 w-5 text-[var(--text-ter)]" />
                          ) : (
                            <Circle className="h-5 w-5 text-[var(--text-ter)]" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--text-pri)] truncate">{item.title}</div>
                          {item.description && (
                            <div className="text-xs text-[var(--text-ter)] truncate">{item.description}</div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {searchQuery.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      const newId = crypto.randomUUID();
                      createItem({ id: newId, title: searchQuery.trim(), createdBy: 'unknown', tags: [] });
                      handleToggleItem(newId);
                      setSearchQuery('');
                    }}
                    className="w-full flex items-center gap-2 p-3 text-[var(--text-ter)] hover:bg-[var(--slate-dim)] border-t border-[var(--rule)] transition-colors"
                  >
                    <Plus className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">Create item: "{searchQuery.trim()}"</span>
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Rationale {mode === 'create' && <span className="text-[var(--coral)]">*</span>}
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why are these items grouped as a package?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={
              isCreating ||
              packageCreationIsBlocked ||
              (mode === 'create' ? selectedItemIds.length < 2 || !rationale.trim() : !existingPackageId)
            }
            className="px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Saving...' : mode === 'add' ? 'Add to package' : 'Create package'}
          </button>
        </div>
      </div>
    </div>
  );
}
